"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SecureScaleRelayUploadRequest = exports.SecureScaleObservationOrigin = exports.SecureScaleEvidenceKind = void 0;
exports.secureScaleEvidenceOriginsMatch = secureScaleEvidenceOriginsMatch;
exports.hasSecureScaleV2Evidence = hasSecureScaleV2Evidence;
exports.hasSecureScaleCheckContext = hasSecureScaleCheckContext;
exports.validSecureScaleEvidence = validSecureScaleEvidence;
const zod_1 = require("zod");
const audit_finding_js_1 = require("./audit-finding.js");
const lane_state_js_1 = require("./lane-state.js");
const weight_js_1 = require("./weight.js");
exports.SecureScaleEvidenceKind = zod_1.z.enum([
    'weight-sample',
    'weight-sample-audited',
    'weight-sample-v2-provenance',
    'weight-sample-v2-origin',
    'weight-sample-v2-check-context',
    'audit-finding',
    'audit-finding-origin',
    'lane-state',
]);
exports.SecureScaleObservationOrigin = zod_1.z
    .object({
    tenantId: zod_1.z.string().uuid(),
    storeId: zod_1.z
        .string()
        .min(1)
        .max(128)
        .refine((value) => value.trim().length > 0),
    versionId: zod_1.z
        .string()
        .min(1)
        .max(128)
        .refine((value) => value.trim().length > 0),
})
    .passthrough();
function secureScaleEvidenceOriginsMatch(body, scope, requireAll = false) {
    const parsed = zod_1.z
        .object({
        records: zod_1.z.array(zod_1.z
            .object({
            schemaVersion: zod_1.z.number().optional(),
            originScope: exports.SecureScaleObservationOrigin.optional(),
        })
            .passthrough()),
    })
        .safeParse(body);
    return (parsed.success &&
        parsed.data.records.every((record) => (!requireAll && record.schemaVersion !== 2) ||
            (record.originScope !== undefined &&
                record.originScope.tenantId === scope.tenantId &&
                record.originScope.storeId === scope.storeId)));
}
/** Route selection inspects schema version, never invents historical provenance. */
function hasSecureScaleV2Evidence(body) {
    const parsed = zod_1.z
        .object({ records: zod_1.z.array(zod_1.z.object({ schemaVersion: zod_1.z.number().optional() }).passthrough()) })
        .safeParse(body);
    return parsed.success && parsed.data.records.some((record) => record.schemaVersion === 2);
}
/** Original check facts require their preserving handler, including replays. */
function hasSecureScaleCheckContext(body) {
    const parsed = zod_1.z
        .object({
        records: zod_1.z.array(zod_1.z
            .object({ schemaVersion: zod_1.z.number().optional(), checkContext: zod_1.z.unknown().optional() })
            .passthrough()),
    })
        .safeParse(body);
    return (parsed.success &&
        parsed.data.records.some((record) => record.schemaVersion === 2 && record.checkContext !== undefined));
}
function validSecureScaleEvidence(body, kind) {
    if (kind === 'audit-finding-origin') {
        const audit = zod_1.z
            .object({
            records: zod_1.z
                .array(audit_finding_js_1.AuditFindingRecord.and(zod_1.z.object({ originScope: exports.SecureScaleObservationOrigin })))
                .min(1)
                .max(5000),
        })
            .safeParse(body);
        if (!audit.success)
            return false;
    }
    if (kind === 'weight-sample-v2-origin' || kind === 'weight-sample-v2-check-context') {
        const origin = zod_1.z
            .object({
            records: zod_1.z.array(zod_1.z
                .object({
                schemaVersion: zod_1.z.number().optional(),
                originScope: exports.SecureScaleObservationOrigin.optional(),
            })
                .passthrough()),
        })
            .safeParse(body);
        if (!origin.success ||
            origin.data.records.some((record) => record.schemaVersion === 2 && record.originScope === undefined))
            return false;
    }
    if (kind === 'weight-sample-v2-provenance' ||
        kind === 'weight-sample-v2-origin' ||
        kind === 'weight-sample-v2-check-context') {
        const versioned = zod_1.z
            .object({
            records: zod_1.z.array(zod_1.z
                .object({
                schemaVersion: zod_1.z.number().optional(),
                cashierKeyProvenance: zod_1.z.enum(['clear', 'applied', 'unknown']).optional(),
                audited: zod_1.z.boolean().optional(),
            })
                .passthrough()),
        })
            .safeParse(body);
        if (!versioned.success ||
            versioned.data.records.some((record) => record.schemaVersion === 2 && record.cashierKeyProvenance === undefined))
            return false;
    }
    if (kind === 'weight-sample' || kind === 'weight-sample-audited') {
        const audited = zod_1.z
            .object({ records: zod_1.z.array(zod_1.z.object({ audited: zod_1.z.boolean().optional() }).passthrough()) })
            .safeParse(body);
        if (!audited.success ||
            audited.data.records.some((record) => kind === 'weight-sample-audited' ? record.audited !== true : record.audited === true))
            return false;
    }
    return (kind === 'audit-finding' || kind === 'audit-finding-origin'
        ? zod_1.z.object({ records: zod_1.z.array(audit_finding_js_1.AuditFindingRecord).min(1).max(5000) })
        : kind === 'lane-state'
            ? lane_state_js_1.LaneStatesUploadRequest
            : weight_js_1.WeightSamplesUploadRequest).safeParse(body).success;
}
exports.SecureScaleRelayUploadRequest = zod_1.z
    .object({
    terminalId: zod_1.z.string().min(1).max(128),
    idempotencyKey: zod_1.z.string().min(1).max(128),
    kind: exports.SecureScaleEvidenceKind.optional(),
    body: zod_1.z.unknown(),
})
    .superRefine((value, ctx) => {
    if (!validSecureScaleEvidence(value.body, hasSecureScaleCheckContext(value.body)
        ? 'weight-sample-v2-check-context'
        : hasSecureScaleV2Evidence(value.body) &&
            (value.kind === undefined ||
                value.kind === 'weight-sample' ||
                value.kind === 'weight-sample-audited' ||
                value.kind === 'weight-sample-v2-provenance')
            ? 'weight-sample-v2-origin'
            : (value.kind ?? 'weight-sample')))
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['body'],
            message: 'invalid evidence envelope',
        });
});
//# sourceMappingURL=securescale-relay.js.map